/**
 * Google Places 搜索组件
 * 封装自动补全、地点坐标获取、UI 交互等功能
 */
window.GooglePlacesSearch = window.GooglePlacesSearch || class GooglePlacesSearch {
    /**
     * 配置选项
     * @typedef {Object} SearchConfig
     * @property {HTMLElement} container - 容器元素（包含 input、list、clearBtn）
     * @property {string} notResultText - 无搜索结果提示文案
     * @property {Function} onSelect - 选中地点后的回调函数 (coordinates, placeDetails) => void
     * @property {Object} [mapInstance] - 地图实例（可选，用于联动）
     * @property {string} [country] - 国家限制，默认从 window.site 读取
     * @property {number} [debounceMs] - 输入防抖毫秒数，默认 500
     * @property {number} [minInputLength] - 触发搜索的最少字符数，默认 2
     */

    /**
     * @param {SearchConfig} config 
     */
    constructor(config) {
        this.container = config.container;
        this.notResultText = config.notResultText || 'Address not found';
        this.onSelect = config.onSelect;
        this.mapInstance = config.mapInstance || null;
        this.country = config.country || (window.site === 'TEST_US' ? 'US' : window.site);
        this.debounceMs = config.debounceMs || 500;
        this.minInputLength = config.minInputLength || 2;

        // DOM 元素缓存
        this.elements = {
            input: this.container.querySelector('.measure-search-input'),
            resultList: this.container.querySelector('.search_result_list'),
            clearBtn: this.container.querySelector('.input_clear')
        };

        // 状态管理
        this.state = {
            predictions: [],
            selectedPlace: null,
            isLoading: false
        };

        // 防抖和请求缓存
        this.debounceTimer = null;
        this.predictionsCache = new Map();
        this.placeDetailsCache = new Map();
        this.sessionToken = null;

        // Google 服务实例（延迟初始化）
        this.autocompleteService = null;
        this.placesService = null;

        this.init();
    }

    /**
     * 初始化组件
     */
    async init() {
        await this.loadGoogleLibraries();
        this.bindEvents();
        this.elements.resultList.style.display = 'none';
        this.elements.clearBtn.style.display = 'none';
    }

    /**
     * 加载 Google Maps 相关库
     */
    async loadGoogleLibraries() {
        try {
            await google.maps.importLibrary('places');
            this.autocompleteService = new google.maps.places.AutocompleteService();
            this.placesService = new google.maps.places.PlacesService(
                document.createElement('div')
            );
        } catch (error) {
            console.error('Failed to load Google Maps libraries:', error);
            throw error;
        }
    }

    getSessionToken() {
        if (!this.sessionToken) {
            this.sessionToken = new google.maps.places.AutocompleteSessionToken();
        }
        return this.sessionToken;
    }

    resetSessionToken() {
        this.sessionToken = null;
    }

    /**
     * 绑定事件监听
     */
    bindEvents() {
        const {
            input,
            resultList,
            clearBtn
        } = this.elements;

        // 输入处理（带防抖）
        input.addEventListener('input', (e) => this.handleInput(e));

        // 焦点管理
        input.addEventListener('focus', () => this.showResultsIfAny());
        input.addEventListener('blur', () => this.hideResultsWithDelay());
        input.addEventListener('keydown', (e) => this.handleKeydown(e));

        // 结果列表点击
        resultList.addEventListener('click', (e) => this.handleResultClick(e));

        // 清除按钮
        clearBtn.addEventListener('click', () => this.clear());

        // 点击外部关闭
        document.addEventListener('click', (e) => {
            if (!this.container.contains(e.target)) {
                this.hideResults();
            }
        });
    }

    /**
     * 处理输入事件
     */
    handleInput(e) {
        const value = e.target.value.trim();

        // 清除按钮显示状态
        this.elements.clearBtn.style.display = value ? 'block' : 'none';
        clearTimeout(this.debounceTimer);

        // 清空结果
        if (!value) {
            this.resetSessionToken();
            this.clearResults();
            return;
        }

        if (value.length < this.minInputLength) {
            this.clearResults();
            return;
        }

        // 防抖搜索
        this.debounceTimer = setTimeout(() => {
            this.fetchPredictions(value);
        }, this.debounceMs);
    }

    /**
     * 获取地点预测
     */
    fetchPredictions(input) {
        if (!this.autocompleteService) return;

        const cacheKey = input.toLowerCase();
        if (this.predictionsCache.has(cacheKey)) {
            this.getSessionToken();
            this.handlePredictionsResponse(this.predictionsCache.get(cacheKey), 'OK');
            return;
        }

        const request = this.buildAutocompleteOptions(input)

        this.autocompleteService.getPlacePredictions(
            request,
            (predictions, status) => {
                if (status === 'OK' && predictions) {
                    this.predictionsCache.set(cacheKey, predictions);
                }
                this.handlePredictionsResponse(predictions, status);
            }
        );
    }
    buildAutocompleteOptions(input) {
        const baseOptions = {
            input: input,
            types: ['geocode'],
            sessionToken: this.getSessionToken()
        };
        if (this.country in window.europeanCountries) {
            return {
                ...baseOptions,
                locationRestriction: {
                    east: 46.450490,
                    west: -12.787790,
                    north: 71.501476,
                    south: 32.854428
                },
                strictBounds: true
            };
        } else {
            // 普通模式：按国家限制
            return {
                ...baseOptions,
                componentRestrictions: {
                    country: this.country.toLowerCase()
                }
            };
        }
    }

    /**
     * 处理预测结果
     */
    handlePredictionsResponse(predictions, status) {
        if (status !== 'OK' || !predictions) {
            const html = this.createNotResultItemHTML();
            this.elements.resultList.innerHTML = html;
            this.elements.resultList.style.display = 'flex';
            return;
        }

        this.state.predictions = predictions;
        this.renderPredictions(predictions);
    }

    /**
     * 渲染预测列表
     */
    renderPredictions(predictions) {
        const html = predictions.map(item => this.createResultItemHTML(item)).join('');
        this.elements.resultList.innerHTML = html;
        this.elements.resultList.style.display = 'flex';
    }

    /**
     * 创建结果项 HTML
     */
    createResultItemHTML(item) {
        return `
        <div class="search-result-item search_result_item_style" data-placeid="${item.place_id}">
          <div class="search_list_icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3.20001 6.31498C3.20001 3.70998 5.35001 1.59998 8.00001 1.59998C10.65 1.59998 12.8 3.70998 12.8 6.31498C12.8 9.29748 9.79501 12.8725 8.54001 14.235C8.24501 14.555 7.75251 14.555 7.45751 14.235C6.20251 12.8725 3.19751 9.29748 3.19751 6.31498H3.20001ZM8.00001 7.99998C8.42436 7.99998 8.83132 7.8314 9.13138 7.53135C9.43144 7.23129 9.60001 6.82432 9.60001 6.39998C9.60001 5.97563 9.43144 5.56866 9.13138 5.2686C8.83132 4.96855 8.42436 4.79998 8.00001 4.79998C7.57566 4.79998 7.1687 4.96855 6.86864 5.2686C6.56858 5.56866 6.40001 5.97563 6.40001 6.39998C6.40001 6.82432 6.56858 7.23129 6.86864 7.53135C7.1687 7.8314 7.57566 7.99998 8.00001 7.99998Z" fill="currentColor" fill-opacity="0.44"/>
            </svg>
          </div>    
          <span class="search_list_text">${this.escapeHtml(item.description)}</span>
        </div>
      `;
    }
    createNotResultItemHTML() {
        return `
        <div class="search_not_result_item search_result_item_style">
          <div class="search_list_icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3.20001 6.31498C3.20001 3.70998 5.35001 1.59998 8.00001 1.59998C10.65 1.59998 12.8 3.70998 12.8 6.31498C12.8 9.29748 9.79501 12.8725 8.54001 14.235C8.24501 14.555 7.75251 14.555 7.45751 14.235C6.20251 12.8725 3.19751 9.29748 3.19751 6.31498H3.20001ZM8.00001 7.99998C8.42436 7.99998 8.83132 7.8314 9.13138 7.53135C9.43144 7.23129 9.60001 6.82432 9.60001 6.39998C9.60001 5.97563 9.43144 5.56866 9.13138 5.2686C8.83132 4.96855 8.42436 4.79998 8.00001 4.79998C7.57566 4.79998 7.1687 4.96855 6.86864 5.2686C6.56858 5.56866 6.40001 5.97563 6.40001 6.39998C6.40001 6.82432 6.56858 7.23129 6.86864 7.53135C7.1687 7.8314 7.57566 7.99998 8.00001 7.99998Z" fill="currentColor" fill-opacity="0.44"/>
            </svg>
          </div>    
          <span class="search_list_text">${this.notResultText}</span>
        </div>
      `;
    }

    /**
     * HTML 转义防止 XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    /**
     * 处理结果点击
     */
    handleResultClick(e) {
        const item = e.target.closest('.search-result-item');
        if (!item) return;

        const placeId = item.getAttribute('data-placeid');
        const description = item.querySelector('.search_list_text').textContent.trim() || '';

        // 更新输入框
        this.elements.input.value = description;
        this.hideResults();

        // 获取坐标
        this.fetchPlaceDetails(placeId);
        // 事件追踪：点击下拉列表后上报 dataLayer 事件
        this.pushGA4Event();
    }

    pushGA4Event() {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "ga4Event",
            event_name: 'nrtk-search-submit',
            event_parameters: {
                current_page: window.location.href,
            }
        });
    }

    /**
     * 获取地点坐标
     */
    fetchPlaceDetails(placeId) {
        if (!this.placesService) return;

        if (this.placeDetailsCache.has(placeId)) {
            this.handlePlaceDetailsResponse(this.placeDetailsCache.get(placeId), 'OK');
            return;
        }

        this.placesService.getDetails({
            placeId,
            fields: ['geometry', 'formatted_address', 'name', 'place_id'],
            sessionToken: this.getSessionToken()
        }, (place, status) => {
            if (status === 'OK' && place) {
                this.placeDetailsCache.set(placeId, place);
            }
            this.handlePlaceDetailsResponse(place, status);
        });
    }

    /**
     * 处理地点坐标响应
     */
    handlePlaceDetailsResponse(place, status) {
        if (status !== 'OK' || !place || !place.geometry) {
            console.error('Failed to get place coordinates:', status);
            this.resetSessionToken();
            return;
        }

        const coordinates = [
            place.geometry.location.lng(),
            place.geometry.location.lat()
        ];

        this.state.selectedPlace = {
            coordinates,
            details: place
        };

        // 触发回调
        if (typeof this.onSelect === 'function') {
            this.onSelect(coordinates, place);
        }

        this.resetSessionToken();
    }

    /**
     * 键盘事件处理
     */
    handleKeydown(e) {
        if (e.key === 'Escape') {
            this.hideResults();
            this.elements.input.blur();
        }
    }

    /**
     * 显示结果（如果有内容）
     */
    showResultsIfAny() {
        if (this.elements.resultList.innerHTML.trim() !== '') {
            this.elements.resultList.style.display = 'flex';
        }
    }

    /**
     * 延迟隐藏结果（允许点击事件触发）
     */
    hideResultsWithDelay() {
        setTimeout(() => this.hideResults(), 200);
    }

    /**
     * 立即隐藏结果
     */
    hideResults() {
        this.elements.resultList.style.display = 'none';
    }

    /**
     * 清空结果
     */
    clearResults() {
        this.elements.resultList.innerHTML = '';
        this.state.predictions = [];
        this.hideResults();
    }

    /**
     * 清空所有（输入+结果）
     */
    clear() {
        this.elements.input.value = '';
        this.elements.clearBtn.style.display = 'none';
        this.resetSessionToken();
        this.clearResults();
        this.state.selectedPlace = null;
    }

    /**
     * 销毁实例
     */
    destroy() {
        clearTimeout(this.debounceTimer);
        this.resetSessionToken();
        // 移除事件监听（如果需要严格清理，需要保存引用）
        this.elements.resultList.innerHTML = '';
    }

    /**
     * 获取当前选中地点
     */
    getSelectedPlace() {
        return this.state.selectedPlace;
    }
}